import React, { Suspense, useEffect, useRef, useState } from 'react';
import { persistQueryClientRestore, persistQueryClientSubscribe } from '@tanstack/react-query-persist-client';
import { useFirebaseAuth, useMaintenanceMode, useMinSupportedVersion, UserContext } from "./services/firebase";
import { buster, persister, queryClient } from './services/queryClient';
import MaintenanceOverlay from './components/MaintenanceOverlay';
import AppUpdateGate from './components/AppUpdateGate';
import { analyticsService } from './services/analytics';
import { BrowserRouter, Routes, Route, Navigate, } from "react-router-dom"; // Switch, Redirect, useLocation
import PageAnalytics from './components/PageAnalytics';
import * as Sentry from "@sentry/react";
// PAGES
import NewBooking from './pages/newBooking/NewBooking';
import SignIn from "./pages/SignIn";
import NewUserInfo from "./pages/NewUserInfo";
import EditClient from './pages/EditClient';
import EditUser from './pages/EditUser';
import EditTalent from './pages/EditTalent';
import Roster from './pages/Roster';
import Test from './pages/dev/Test';
import Buttons from './pages/dev/Buttons';
import Alerts from './pages/dev/Alerts';
import Palette from './pages/dev/Palette';
import Preview from './pages/dev/Preview';
import SharedUi from './pages/dev/SharedUi';
import Lists from './pages/Lists';
import EditList from './pages/editList';
import { RecommendTest } from './pages/RecommendTest';
import PublicList from './pages/PublicList';

import { Loader2 } from 'lucide-react';
import Campaigns from './pages/Campaigns';
import CampaignDetailsPage from './pages/campaignDetails';
import CampaignForm from './pages/campaignForm';
import Pitching from './pages/pitching';
import Opportunities from './pages/opportunities';
import MyPitches from './pages/myPitches';
import { Toaster } from '@/components/ui/toast';

const Bookings = React.lazy(() => import('./pages/Bookings'));
const BookingsSearch = React.lazy(() => import('./pages/BookingsSearch'));
const Managers = React.lazy(() => import('./pages/Managers'));
const AirtableOperationsAdmin = React.lazy(() => import('./pages/AirtableOperationsAdmin'));

const fullScreenLoader = (
    <div className="flex h-screen w-screen items-center justify-center"><Loader2 className="size-10 animate-spin text-primary" aria-label="Loading" /></div>
);

if (import.meta.env.DEV) {
    console.log("Development; won't init Sentry");
} else {
    Sentry.init({
        environment: import.meta.env.MODE,
        dsn: "https://d0dd3e04e48945d283ea7773835a8daa@o4504368797908992.ingest.sentry.io/4504368801185792",
        // v8: class integrations → functional (https://docs.sentry.io/platforms/javascript/guides/react/migration/v7-to-v8/)
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({ maskAllText: false, maskAllInputs: false }),
        ],
        release: "grail-app@" + import.meta.env.VITE_APP_VERSION,

        // https://docs.sentry.io/platforms/javascript/guides/nextjs/configuration/filtering/#using--1
        ignoreErrors: [
            "IDBDatabase",
            "Network Error",
            "network error",
            "timed out",
            "client is offline",
            "Non-Error exception captured",
            "Non-Error promise rejection captured"
        ],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 0.2,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
    });
}

const App: React.FC<any> = () => {
    const { userAuthed, userLoading, user } = useFirebaseAuth();
    const maintenance = useMaintenanceMode();
    const minVersion = useMinSupportedVersion();
    const prevMaintenanceActive = useRef<boolean | null>(null);
    const [persistRestored, setPersistRestored] = useState(false);

    useEffect(() => {
        if (!maintenance.ready) return;
        if (prevMaintenanceActive.current === true && maintenance.active === false) {
            window.location.reload();
        }
        prevMaintenanceActive.current = maintenance.active;
    }, [maintenance.ready, maintenance.active]);
    // const location = useLocation(); // <-- get current location being accessed

    useEffect(() => {
        if (user?.id) {
            analyticsService().setUserId(user.id);
        }
    }, [user]);

    useEffect(() => {
        if (!user?.email) {
            setPersistRestored(false);
            return;
        }

        let cancelled = false;
        let unsubscribe: (() => void) | undefined;
        setPersistRestored(false);

        persistQueryClientRestore({ queryClient, persister, buster }).then(() => {
            if (cancelled) return;
            unsubscribe = persistQueryClientSubscribe({ queryClient, persister, buster });
            setPersistRestored(true);
        }).catch(() => {
            if (cancelled) return;
            setPersistRestored(true);
        });

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [user?.email]);

    const isGrailTeam = user?.roles?.manager || user?.roles?.admin || user?.roles?.externalManager || user?.roles?.superAdmin || user?.roles?.salesTeam || user?.roles?.brandTeam;

    return (
        <div>
                <Toaster>
                    <UserContext.Provider value={user}>
                        <BrowserRouter>
                            {userLoading ? ( // NEEDS TO BE FIRST, or we'll get redirects before we've loaded (e.g. if you navigate anywhere besides '/' )
                                (<Routes>
                                    <Route path="*" element={fullScreenLoader} />
                                </Routes>)
                            ) : (
                                <>
                                    {userAuthed?.uid ? (
                                        // PROTECTED ROUTES
                                        (<>
                                            {/* Check if we have a user object yet...*/}
                                            {user?.email && persistRestored ? (
                                                <>
                                                    <PageAnalytics />
                                                    <Suspense fallback={fullScreenLoader}>
                                                    <Routes>
                                                        {user?.roles?.creator && (
                                                            <>
                                                                {!user?.talentFieldsEdited && user?.talentIds?.length === 1 ?
                                                                    (
                                                                        <>
                                                                            <Route path="*" element={<Navigate to={`/edit-talent/${user.talentIds[0]}`} />} />
                                                                            <Route path="/edit-talent/:talentId" element={<EditTalent />} />
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Route path="/" element={<Bookings />} />
                                                                            <Route path="/edit-talent/:talentId" element={<EditTalent />} />
                                                                            <Route path="/creator-roster/:creatorUid" element={<Roster />} />
                                                                        </>
                                                                    )}
                                                            </>
                                                        )}

                                                        {user?.roles?.booker && !isGrailTeam && (
                                                            <>
                                                                {
                                                                    (user?.firstName && user?.lastName) ? (
                                                                        <>
                                                                            <Route path="/" element={<Navigate to="/new-booking" />} />

                                                                            <Route path="/campaign/:campaignId" element={<CampaignDetailsPage />} />
                                                                            <Route path="/campaign/:campaignId/settings" element={<CampaignForm mode="edit" />} />
                                                                            <Route path="/campaign/:campaignId/pitch" element={<Pitching />} />
                                                                            <Route path="/new-opportunity" element={<CampaignForm mode="create" />} />
                                                                            <Route path="/campaigns/">
                                                                                <Route path="/campaigns/" element={<Campaigns />} />
                                                                                <Route path="/campaigns/:uid" element={<Campaigns />} />
                                                                            </Route>

                                                                            <Route path="new-booking">
                                                                                <Route path="/new-booking" element={<NewBooking />} />
                                                                                <Route path="/new-booking/:listId" element={<NewBooking />} />
                                                                            </Route>

                                                                            <Route path="/new-client/:clientId" element={<EditClient />} />
                                                                        </>
                                                                    ) :
                                                                        // <Routes>
                                                                        <Route path="*" element={<>
                                                                            <PageAnalytics pageName="new-user" />
                                                                            <NewUserInfo />
                                                                        </>} />
                                                                    // </Routes>
                                                                }

                                                            </>
                                                        )}

                                                        {isGrailTeam && (
                                                            <>
                                                                <Route path="/" element={<Bookings />} />

                                                                <Route path="/edit-client/:clientId" element={<EditClient managerMode={true} adminMode={user?.roles?.admin} />} />
                                                                <Route path="/edit-talent/:talentId" element={<EditTalent />} />
                                                                <Route path="/edit-user/:uid" element={<EditUser />} />

                                                                <Route path="/campaign/:campaignId" element={<CampaignDetailsPage />} />
                                                                <Route path="/campaign/:campaignId/settings" element={<CampaignForm mode="edit" />} />
                                                                <Route path="/campaign/:campaignId/pitch" element={<Pitching />} />
                                                                <Route path="/new-opportunity" element={<CampaignForm mode="create" />} />
                                                                <Route path="/opportunities" element={<Opportunities />} />
                                                                <Route path="/my-pitches" element={<MyPitches />} />
                                                                <Route path="/campaigns/">
                                                                    <Route path="/campaigns/" element={<Campaigns />} />
                                                                    <Route path="/campaigns/:uid" element={<Campaigns />} />
                                                                </Route>

                                                                <Route path="new-booking">
                                                                    <Route path="/new-booking" element={<NewBooking />} />
                                                                    <Route path="/new-booking/:listId" element={<NewBooking />} />
                                                                </Route>

                                                                <Route path="/lists/">
                                                                    <Route path="/lists/creator-requests" element={<Lists />} />
                                                                    <Route path="/lists/:uid" element={<Lists />} />
                                                                </Route>
                                                                <Route path="/edit-list/:listId" element={<EditList />} />
                                                                <Route path="/public-list/:listId" element={<PublicList />} />

                                                                <Route path="/new-client/:clientId" element={<EditClient />} />

                                                                <Route path="roster">
                                                                    <Route path="/roster/" element={<Roster />} />
                                                                    <Route path="/roster/:managerUid" element={<Roster />} />
                                                                </Route>

                                                                <Route path="search" element={<BookingsSearch />} />
                                                            </>
                                                        )}

                                                        {
                                                            (user.roles?.admin ? (
                                                                <>
                                                                    <Route path="/managers" element={<Managers />} />
                                                                    <Route path="/admin/airtable-operations" element={<AirtableOperationsAdmin />} />
                                                                    <Route path="recommend-test" element={<RecommendTest />} />
                                                                    <Route path="/dev/test" element={<Test />} />
                                                                    <Route path="/dev/buttons" element={<Buttons />} />
                                                                    <Route path="/dev/alerts" element={<Alerts />} />
                                                                    <Route path="/dev/palette" element={<Palette />} />
                                                                    <Route path="/dev/preview" element={<Preview />} />
                                                                    <Route path="/dev/shared-ui" element={<SharedUi />} />
                                                                    <Route path="/new-user" element={<NewUserInfo />} />
                                                                </>
                                                            ) : null)
                                                        }

                                                        <Route path=":wildcard/*" element={<Navigate to="/" />} />
                                                    </Routes>
                                                    </Suspense>
                                                </>
                                            ) : (
                                                // Keep loading until we've got the full user object and persist has restored...
                                                (<Routes>
                                                    <Route path="*" element={fullScreenLoader} />
                                                </Routes>)
                                            )}
                                        </>)
                                    ) : (
                                        // PUBLIC ROUTES
                                        (<Routes>
                                            <Route path="/new-client/:clientId" element={<>
                                                <PageAnalytics />
                                                <EditClient />
                                            </>} />
                                            <Route path="/public-list/:listId" element={<PublicList />} />
                                            {/* Show SignIn, then redirect on sign-in/up to previous path  */}
                                            <Route path="*" element={<>
                                                <PageAnalytics pageName="sign-in" />
                                                <SignIn />
                                            </>} />
                                        </Routes>)
                                    )}
                                </>
                            )}
                            <AppUpdateGate
                                userLoading={userLoading}
                                maintenanceReady={maintenance.ready}
                                maintenanceActive={maintenance.active}
                                minVersionReady={minVersion.ready}
                                belowMinimum={minVersion.belowMinimum}
                                minSupportedVersion={minVersion.minSupportedVersion}
                            />
                        </BrowserRouter>
                        {maintenance.ready &&
                            maintenance.active &&
                            !(
                                user?.roles?.superAdmin === true &&
                                maintenance.allowSuperAdminDuringMaintenance
                            ) && <MaintenanceOverlay message={maintenance.message} />}
                    </UserContext.Provider>
                </Toaster>
        </div >
    );
}

export default App;